import React, { useEffect, useMemo, useRef, useState } from "react";
import AgencyVerificationBanner from "./AgencyVerificationBanner.jsx";
import FindOfficePagination from "../FindOffice/FindOfficePagination.jsx";
import DynamicOfficeCard from "../DynamicOfficeCard/DynamicOfficeCard.jsx";

const DEFAULT_PAGE_SIZE = 10;

const AgencyVerification = ({
  bannerProps,
  paginationProps,
  isAgencyVerification = false,
}) => {
  const rootRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [locations, setLocations] = useState([]);
  const [validationError, setValidationError] = useState("");
  const [didSearch, setDidSearch] = useState(false);
  const [emptyOverride, setEmptyOverride] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const pageSize = paginationProps?.pageSize ?? DEFAULT_PAGE_SIZE;
  const showPagination = isLoading || didSearch;

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const form = root.querySelector("form.find-office-form");
    if (!form) return;

    const onLoading = e => {
      const loading = !!e.detail?.loading;
      setIsLoading(loading);
      if (loading) {
        setValidationError("");
      }
    };

    const onResults = e => {
      const list = Array.isArray(e.detail?.list) ? e.detail.list : [];
      const status = e.detail?.status;

      setLocations(list);
      setIsLoading(false);
      setValidationError("");
      setCurrentPage(1);
      setDidSearch(true);

      if (status === 404) {
        setEmptyOverride({
          title: "There are no agencies matching the information provided.",
          subtitle:
            "Please modify your search criteria or reach out to Contract Admin to confirm the status of the agent.",
        });
      } else {
        setEmptyOverride(null);
      }
    };

    const onError = e => {
      const { status, kind, message } = e.detail || {};

      if (kind === "validation") {
        setIsLoading(false);
        setValidationError(message || "Please enter a city or agency name");
        return;
      }

      if (status === 404) {
        setLocations([]);
        setIsLoading(false);
        setValidationError("");
        setDidSearch(true);
        setEmptyOverride({
          title: "There are no agencies matching the information provided.",
          subtitle:
            "Please modify your search criteria or reach out to Contract Admin to confirm the status of the agent.",
        });
        return;
      }

      setIsLoading(false);
      setValidationError("");
      setDidSearch(true);
      setEmptyOverride(null);
    };

    form.addEventListener("findoffice:loading", onLoading);
    form.addEventListener("findoffice:results", onResults);
    form.addEventListener("findoffice:error", onError);

    return () => {
      form.removeEventListener("findoffice:loading", onLoading);
      form.removeEventListener("findoffice:results", onResults);
      form.removeEventListener("findoffice:error", onError);
    };
  }, []);

  const hasLocations = locations.length > 0;

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(locations.length / pageSize)),
    [locations.length, pageSize],
  );

  useEffect(() => {
    setCurrentPage(p => Math.min(p, totalPages));
  }, [totalPages]);

  const start = (currentPage - 1) * pageSize;
  const end = start + pageSize;

  const pagedLocations = useMemo(
    () => locations.slice(start, end),
    [locations, start, end],
  );

  const emptyTitleProp =
    emptyOverride?.title ||
    paginationProps?.noAgenciesMatchTitle ||
    "No results";

  const emptySubtitleProp =
    emptyOverride?.subtitle ||
    paginationProps?.noAgenciesMatchMessage ||
    "Try another search.";

  return (
    <div className="find-office" ref={rootRef}>
      {bannerProps && (
        <AgencyVerificationBanner
          {...bannerProps}
          errorMessage={validationError}
        />
      )}

      {showPagination && (
        <FindOfficePagination
          {...paginationProps}
          isLoading={isLoading}
          loadingMessage={paginationProps?.loadingMessage || "Searching..."}
          noMatchTitle={emptyTitleProp}
          noMatchMessage={emptySubtitleProp}
          locations={locations}
          visibleCount={locations.length}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={page => setCurrentPage(page)}
          isAgencyVerification={isAgencyVerification}
        />
      )}

      <div className="find-office-list" hidden={!hasLocations && !isLoading}>
        <DynamicOfficeCard
          offices={pagedLocations}
          isAgencyVerification={isAgencyVerification}
          showBadgeText={false}
          addressLabel={
            paginationProps?.addressLabel || "Mailing/Shipping Address"
          }
          underwriterLabel={paginationProps?.underwriterLabel || "Underwriter"}
          serviceStateLabel={
            paginationProps?.serviceStateLabel || "Service State"
          }
        />
      </div>
    </div>
  );
};

export default AgencyVerification;
